"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Menu, Bell, CircleHelp, Settings, LayoutDashboard, LogOut, type LucideIcon } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { Avatar } from "@/components/ui/avatar";
import { apiPost } from "@/lib/api-client";
import { cn } from "@/lib/cn";

interface TopbarProps {
  onOpenMobileNav: () => void;
  userName?: string;
  /** Tiêu đề trang hiển thị ở góc trái (vd tên lớp). */
  title?: React.ReactNode;
}

const iconBtn =
  "inline-flex size-10 items-center justify-center rounded-full text-foreground-secondary hover:bg-subtle focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface";

export function Topbar({ onOpenMobileNav, userName, title }: TopbarProps) {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  async function logout() {
    try {
      await apiPost("/api/auth/logout");
    } catch {
      /* vẫn điều hướng về đăng nhập */
    }
    router.push("/dang-nhap");
    router.refresh();
  }

  return (
    <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-surface px-4 lg:px-6">
      {/* Hamburger (mobile) */}
      <button type="button" onClick={onOpenMobileNav} aria-label="Mở menu điều hướng" className={cn(iconBtn, "lg:hidden")}>
        <Menu className="size-5" aria-hidden="true" />
      </button>

      {/* Tiêu đề trang (vd tên lớp) — góc trái */}
      {title && <div className="flex min-w-0 items-center">{title}</div>}

      {/* Đẩy cụm về bên phải */}
      <div className="flex-1" />

      {/* Cụm bên phải: Thông báo · Trợ giúp · Dark mode · Avatar menu */}
      <Link href="/thong-bao" aria-label="Thông báo" className={iconBtn}>
        <Bell className="size-5" aria-hidden="true" />
      </Link>
      <Link href="/tro-giup" aria-label="Trợ giúp" className={iconBtn}>
        <CircleHelp className="size-5" aria-hidden="true" />
      </Link>
      <ThemeToggle />

      <div ref={menuRef} className="relative ml-1">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Tài khoản của bạn"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="rounded-full focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        >
          <Avatar size={32} name={userName ?? "Tài khoản"} />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-30 mt-2 w-56 rounded-xl border border-border bg-surface p-1.5 shadow-lg"
          >
            <div className="flex items-center gap-2.5 px-2.5 py-2">
              <Avatar size={40} name={userName ?? "Tài khoản"} />
              <div className="min-w-0">
                <p className="truncate text-body-sm font-semibold text-foreground">{userName ?? "Tài khoản"}</p>
                <p className="text-caption text-muted">Giáo viên</p>
              </div>
            </div>
            <div className="my-1 h-px bg-border" />
            <MenuLink href="/cai-dat" icon={Settings} label="Tài khoản" onClick={() => setMenuOpen(false)} />
            <MenuLink href="/bang-dieu-khien" icon={LayoutDashboard} label="Bảng điều khiển" onClick={() => setMenuOpen(false)} />
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              role="menuitem"
              onClick={logout}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-body-sm text-danger hover:bg-danger/10"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Đăng xuất
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function MenuLink({
  href,
  icon: Icon,
  label,
  onClick,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <Link
      href={href}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-body-sm text-foreground hover:bg-subtle"
    >
      <Icon className="size-4 text-muted" aria-hidden="true" />
      {label}
    </Link>
  );
}
